/** @jsxImportSource @emotion/react */
import { MutableRefObject, useEffect, useState } from "react";
import Sortable from "sortablejs";
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
  useFormContext,
} from "react-hook-form";
import { MenuProductsFormValues } from "../MenuUpsert";
import { css } from "@emotion/react";
import { breakpoints } from "../../../components/breakpoints";
import { DragAndDropContainer } from "../../../components/DragAndDropContainer";
import { Divider } from "../../../components/Divider";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { AddMenuProductsSelector } from "./AddMenuProductsSelector";
import { MenuCategoryGridItems } from "./MenuCategoryGridItems";
import { OverwriteMenuModalFormValues } from "./OverwriteMenuModal";
import { MenuProduct } from "../models/Menus";

type MenuCategory = {
  categoryName: string;
  categoryId: string;
  categorySortKey: number | string;
  categoryDescription: string;
};

const categoryWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const categoryTitleWrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const categoryHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
`;

const dividerAdditionalStyling = css`
  margin-top: 8px;
  margin-bottom: 8px;
`;

const categoryGridStyle = css`
  display: grid;
  column-gap: 16px;
  row-gap: 16px;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  ${breakpoints.mobile} {
    grid-template-columns: 1fr;
  }
  ${breakpoints.tablet} {
    grid-template-columns: 1fr 1fr;
  }
  ${breakpoints.desktop} {
    grid-template-columns: 1fr 1fr 1fr;
  }
  ${breakpoints.hd} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`;

type Props = {
  remove: UseFieldArrayRemove;
  fields: FieldArrayWithId<
    MenuProductsFormValues | OverwriteMenuModalFormValues,
    "products",
    "id"
  >[];
  append: UseFieldArrayAppend<any, "products">;
  isOverwrite?: boolean;
  update?: UseFieldArrayUpdate<OverwriteMenuModalFormValues, "products">;
  availableProducts: MenuProduct[] | undefined;
  availableProductsAreLoading: boolean;
  originalMenuProducts: MenuProduct[] | undefined;
};

export const MenuCategoryGrid = ({
  remove,
  fields,
  append,
  isOverwrite,
  update,
  availableProducts,
  availableProductsAreLoading,
  originalMenuProducts,
}: Props) => {
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const { setValue } = useFormContext<
    MenuProductsFormValues | OverwriteMenuModalFormValues
  >();

  const onDrop = (
    event: Sortable.SortableEvent,
    containerRef: MutableRefObject<HTMLElement | null>
  ) => {
    if (!containerRef) {
      return;
    }
    // iterating over grid dom children. Each of them has the original index as data attribute
    // @ts-expect-error
    [...containerRef.current?.children].forEach((b, index) => {
      // Index to update in products array. Using setValue, as update from useFieldArray API would rerender component, which is not desired.
      const productIndex = b.dataset.id;
      setValue(`products.${productIndex}.sortKey`, index + 1, {
        // Set form as dirty
        shouldDirty: true,
      });
    });
  };

  useEffect(() => {
    if (!fields) {
      return;
    }
    const categoryIds: string[] = [];
    const categories: MenuCategory[] = [];
    fields.forEach((p) => {
      if (!categoryIds.includes(p.category.id)) {
        categories.push({
          categoryId: p.category.id,
          categoryName: p.category.name,
          categorySortKey: p.category.sortKey,
          categoryDescription: p.category.description ?? "",
        });
        categoryIds.push(p.category.id);
      }
    });
    setCategories(categories);
  }, [fields]);

  return (
    <>
      {categories
        .sort(
          (a, b) =>
            // Can be a string
            Number(a.categorySortKey) - Number(b.categorySortKey)
        )
        .map((c, i) => (
          <div key={i} css={categoryWrapperStyle}>
            <div css={categoryHeaderStyle}>
              <div css={categoryTitleWrapperStyle}>
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderTwoSemibold mb-0"
                >
                  {c.categoryName}
                </StyledText>
                <AddMenuProductsSelector
                  append={append}
                  categoryId={c.categoryId}
                  isOverwrite={isOverwrite}
                  availableProducts={availableProducts}
                  availableProductsAreLoading={availableProductsAreLoading}
                />
              </div>
              <StyledText
                color={colors.textPrimary}
                className="bodySubtextRegular mb-0"
              >
                {c.categoryDescription}
              </StyledText>
            </div>
            <DragAndDropContainer
              onEnd={onDrop}
              containerStyle={categoryGridStyle}
            >
              <MenuCategoryGridItems
                remove={remove}
                isOverwrite={isOverwrite}
                update={update}
                originalMenuProducts={originalMenuProducts}
                products={fields
                  // Need the original index to determine what product to update
                  .map((p, index) => ({
                    ...p,
                    fieldIndex: index,
                  }))
                  // Only want products in category
                  .filter((p) => p.category.id === c.categoryId)
                  // Sorting from sort key
                  .sort(
                    (a, b) =>
                      // Can be a string
                      Number(a.sortKey) - Number(b.sortKey)
                  )}
              />
            </DragAndDropContainer>
            <Divider additionalStyling={dividerAdditionalStyling} />
          </div>
        ))}
    </>
  );
};
